import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../../../../shared/components/card/Card";
import { Checkbox } from "../../../../../../shared/components/checkbox/Checkbox";
import { Button } from "../../../../../../shared/components/button/Button";
import { CheckSquare, Search, Square, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { Input } from "../../../../../../shared/components/input/Input";
import { Badge } from "../../../../../../shared/components/badge/Badge";
import { ScrollArea } from "../../../../../../shared/components/scroll-area/ScrollArea";

const DimensionMetricSelector = ({
  dimensions,
  metrics,
  selectedDimensions,
  selectedMetrics,
  onDimensionsChange,
  onMetricsChange,
}) => {
  const [dimensionSearch, setDimensionSearch] = useState("");
  const [metricSearch, setMetricSearch] = useState("");

  const filteredDimensions = useMemo(() => {
    return dimensions.filter((dimension) =>
      dimension.name.toLowerCase().includes(dimensionSearch.toLowerCase()),
    );
  }, [dimensions, dimensionSearch]);

  const filteredMetrics = useMemo(() => {
    return metrics.filter((metric) =>
      metric.name.toLowerCase().includes(metricSearch.toLowerCase()),
    );
  }, [metrics, metricSearch]);

  const toggleDimension = (dimensionCode) => {
    if (selectedDimensions.includes(dimensionCode)) {
      onDimensionsChange(
        selectedDimensions.filter((code) => code !== dimensionCode),
      );
    } else {
      onDimensionsChange([...selectedDimensions, dimensionCode]);
    }
  };

  const toggleMetric = (metricCode) => {
    if (selectedMetrics.includes(metricCode)) {
      onMetricsChange(selectedMetrics.filter((code) => code !== metricCode));
    } else {
      onMetricsChange([...selectedMetrics, metricCode]);
    }
  };

  const selectAllVisibleDimensions = () => {
    const visibleIds = filteredDimensions.map((d) => d.code);
    const newSelected = [...new Set([...selectedDimensions, ...visibleIds])];
    onDimensionsChange(newSelected);
  };

  const selectAllVisibleMetrics = () => {
    const visibleIds = filteredMetrics.map((m) => m.code);
    const newSelected = [...new Set([...selectedMetrics, ...visibleIds])];
    onMetricsChange(newSelected);
  };

  const deselectAllVisibleDimensions = () => {
    const visibleIds = filteredDimensions.map((d) => d.code);
    const newSelected = selectedDimensions.filter(
      (code) => !visibleIds.includes(code),
    );
    onDimensionsChange(newSelected);
  };

  const deselectAllVisibleMetrics = () => {
    const visibleIds = filteredMetrics.map((m) => m.code);
    const newSelected = selectedMetrics.filter(
      (code) => !visibleIds.includes(code),
    );
    onMetricsChange(newSelected);
  };

  const allVisibleDimensionsSelected =
    filteredDimensions.length > 0 &&
    filteredDimensions.every((d) => selectedDimensions.includes(d.code));

  const allVisibleMetricsSelected =
    filteredMetrics.length > 0 &&
    filteredMetrics.every((m) => selectedMetrics.includes(m.code));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CardTitle>Dimensions</CardTitle>
              {selectedDimensions.length > 0 && (
                <Badge variant="secondary">
                  {selectedDimensions.length} selected
                </Badge>
              )}
            </div>
            <div className="flex gap-1">
              {filteredDimensions.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={
                    allVisibleDimensionsSelected
                      ? deselectAllVisibleDimensions
                      : selectAllVisibleDimensions
                  }
                  className="text-xs"
                >
                  {allVisibleDimensionsSelected ? (
                    <>
                      <Square className="w-3 h-3 mr-1" />
                      Deselect All
                    </>
                  ) : (
                    <>
                      <CheckSquare className="w-3 h-3 mr-1" />
                      Select All
                    </>
                  )}
                </Button>
              )}
              {selectedDimensions.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onDimensionsChange([])}
                  className="text-muted-foreground text-xs"
                >
                  <Trash2 className="w-3 h-3 mr-1" />
                  Clear
                </Button>
              )}
            </div>
          </div>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search dimensions..."
              value={dimensionSearch}
              onChange={(e) => setDimensionSearch(e.target.value)}
              className="pl-10 h-9"
            />
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <ScrollArea className="h-80">
            <div className="grid grid-cols-1 gap-2">
              {filteredDimensions.length === 0 ? (
                <div className="text-center text-muted-foreground py-4">
                  {dimensionSearch
                    ? `No dimensions found matching "${dimensionSearch}"`
                    : "No dimensions available"}
                </div>
              ) : (
                filteredDimensions.map((dimension) => (
                  <div
                    key={dimension.code}
                    className="flex items-center space-x-2 p-2 rounded hover:bg-accent cursor-pointer"
                    onClick={() => toggleDimension(dimension.code)}
                  >
                    <Checkbox
                      id={dimension.code}
                      checked={selectedDimensions.includes(dimension.code)}
                      onCheckedChange={() => toggleDimension(dimension.code)}
                    />
                    <label
                      htmlFor={dimension.code}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer flex-1"
                    >
                      {dimension.name}
                      <span className="text-xs text-muted-foreground ml-2">
                        ({dimension.dimensionType.name})
                      </span>
                    </label>
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CardTitle>Metrics</CardTitle>
              {selectedMetrics.length > 0 && (
                <Badge variant="secondary">
                  {selectedMetrics.length} selected
                </Badge>
              )}
            </div>
            <div className="flex gap-1">
              {filteredMetrics.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={
                    allVisibleMetricsSelected
                      ? deselectAllVisibleMetrics
                      : selectAllVisibleMetrics
                  }
                  className="text-xs"
                >
                  {allVisibleMetricsSelected ? (
                    <>
                      <Square className="w-3 h-3 mr-1" />
                      Deselect All
                    </>
                  ) : (
                    <>
                      <CheckSquare className="w-3 h-3 mr-1" />
                      Select All
                    </>
                  )}
                </Button>
              )}
              {selectedMetrics.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onMetricsChange([])}
                  className="text-muted-foreground text-xs"
                >
                  <Trash2 className="w-3 h-3 mr-1" />
                  Clear
                </Button>
              )}
            </div>
          </div>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search metrics..."
              value={metricSearch}
              onChange={(e) => setMetricSearch(e.target.value)}
              className="pl-10 h-9"
            />
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <ScrollArea className="h-80">
            <div className="grid grid-cols-1 gap-2">
              {filteredMetrics.length === 0 ? (
                <div className="text-center text-muted-foreground py-4">
                  {metricSearch
                    ? `No metrics found matching "${metricSearch}"`
                    : "No metrics available"}
                </div>
              ) : (
                filteredMetrics.map((metric) => (
                  <div
                    key={metric.code}
                    className="flex items-center space-x-2 p-2 rounded hover:bg-accent cursor-pointer"
                    onClick={() => toggleMetric(metric.code)}
                  >
                    <Checkbox
                      id={metric.code}
                      checked={selectedMetrics.includes(metric.code)}
                      onCheckedChange={() => toggleMetric(metric.code)}
                    />
                    <label
                      htmlFor={metric.code}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer flex-1"
                    >
                      {metric.name}
                      <span className="text-xs text-muted-foreground ml-2">
                        ({metric.aggregationFunction.name})
                      </span>
                    </label>
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
};

export default DimensionMetricSelector;
